import React, { useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import { Button, Col, Row, Form as RBForm, Card, Spinner } from 'react-bootstrap';

import SvgUpdate from 'assets/customizeIcons/svg-icons/UpdateIcon';
import CloseSquare from 'assets/customizeIcons/CloseSquare.svg';
import AddIcon from 'assets/customizeIcons/AddIcon.svg';
import { COLORS } from './Mobile';
import { useLogic } from 'pages/Clients/hooks/useLogic';
import showNotification from 'components/extras/showNotification';
import SvgMobile from 'assets/customizeIcons/svg-icons/MobileIcon';
import SvgMap from 'assets/customizeIcons/svg-icons/MapIcon';

function ClientTabMobile({ clientData, setInterventionData }) {
	const [toUpdate, setToUpdate] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const defaultClientValues = {
		nom: clientData?.nom || '',
		telephones: clientData?.telephones || [],
		emails: clientData?.emails || [],
		shippingAddresses: clientData?.shippingAddresses || [],
		typeOccupation: clientData?.typeOccupation || '',
		interphone: clientData?.interphone || '',
		numeroAppartement: clientData?.numeroAppartement || '',
		numeroProprietaire: clientData?.numeroProprietaire || '',
		codeAcces: clientData?.codeAcces || '',
	};
	const { updateClient } = useLogic();
	const handleSave = async (values) => {
		setIsSubmitting(true);
		try {
			// Clean arrays
			const telephones = values.telephones.filter((t) => t.trim() !== '');
			const emails = values.emails.filter((e) => e.trim() !== '');

			// Clean addresses: remove empty ones, but keep structure
			const cleanAddress = (addr) => {
				const cleaned = { ...addr };
				// Only keep if at least one field has content
				const hasContent = ['formatted', 'street', 'city', 'zip', 'state', 'country'].some(
					(key) => cleaned[key]?.trim(),
				);
				const hasValidCoords = cleaned.location?.coordinates?.every(
					(c) => typeof c === 'number' && !isNaN(c),
				);
				if (
					!hasContent &&
					(!hasValidCoords ||
						(cleaned.location.coordinates[0] === 0 &&
							cleaned.location.coordinates[1] === 0))
				) {
					return null;
				}
				// Ensure location.type is set
				if (cleaned.location && hasValidCoords) {
					cleaned.location.type = 'Point';
				} else {
					delete cleaned.location;
				}
				return cleaned;
			};

			const shippingAddresses = values?.shippingAddresses?.map(cleanAddress).filter(Boolean);

			const submitData = {
				nom: values.nom.trim(),
				type: values.type,
				interphone: values.interphone?.trim() || undefined,
				numeroAppartement: values.numeroAppartement?.trim() || undefined,
				numeroProprietaire: values.numeroProprietaire?.trim() || undefined,
				codeAcces: values.codeAcces?.trim() || undefined,
				telephones,
				emails,
				shippingAddresses,
			};

			const result = await updateClient(clientData?._id, submitData);
			if (result.success) {
				showNotification('Succès', `Client modifié avec succès`, 'success');
				console.log('result', result);
				setInterventionData((prev) => ({ ...prev, client: result?.data }));
				setToUpdate(false);
			} else {
				showNotification('Erreur', 'Erreur inattendue', 'warning');
			}
		} catch (err) {
			showNotification('Erreur', err.message || 'Erreur inattendue', 'warning');
		} finally {
			setIsSubmitting(false);
		}
	};
	const handlePhoneWeb = (phones = []) => {
		if (!phones.length) {
			alert('Aucun numéro de téléphone disponible');
			return;
		}

		// One number → dial link
		if (phones.length === 1) {
			window.location.href = `tel:${phones[0]}`;
			return;
		}

		// Multiple numbers → choose
		const choice = prompt(
			'Choisissez un numéro :\n\n' + phones.map((p, i) => `${i + 1}. ${p}`).join('\n'),
		);

		const index = Number(choice) - 1;
		if (!isNaN(index) && phones[index]) {
			window.location.href = `tel:${phones[index]}`;
		}
	};

	return (
		<div className='devis-container'>
			{/* HEADER */}
			<div className='devis-header'>
				<h3>Informations du client</h3>

				<div onClick={() => setToUpdate(!toUpdate)}>
					<SvgUpdate color={COLORS.DEFAULT} size={25} />
				</div>
			</div>

			{toUpdate ? (
				<Formik
					initialValues={defaultClientValues}
					validate={(values) => {
						const errors = {};
						if (!values.nom) errors.nom = 'Le nom est obligatoire';
						if (!values.telephones || values.telephones.length === 0)
							errors.telephones = 'Au moins un numéro de téléphone est requis';
						if (
							values.typeOccupation === 'locataire' &&
							(!values.numeroProprietaire || !values.numeroProprietaire.trim())
						)
							errors.numeroProprietaire =
								'Numéro du propriétaire requis pour les locataires';
						return errors;
					}}
					onSubmit={(values) => {
						handleSave(values);
					}}>
					{({ values, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
						<Form>
							<Row className='mb-3'>
								<Col md={6}>
									<RBForm.Group>
										<RBForm.Label>Nom</RBForm.Label>
										<RBForm.Control
											type='text'
											name='nom'
											value={values.nom}
											onChange={handleChange}
											onBlur={handleBlur}
										/>
										<ErrorMessage
											name='nom'
											component='div'
											className='text-danger'
										/>
									</RBForm.Group>
								</Col>
							</Row>
							<Row className='mb-3'>
								<Col md={12}>
									<div className='d-flex justify-content-between align-items-center mb-2'>
										<RBForm.Label className='mb-0'>Emails</RBForm.Label>
										<div
											onClick={() =>
												setFieldValue('emails', [...values.emails, ''])
											}
											style={{ cursor: 'pointer' }}>
											<img
												src={AddIcon}
												width={30}
												height={30}
												alt='Ajouter email'
											/>
										</div>
									</div>

									{values.emails?.map((email, idx) => (
										<div key={idx} className='d-flex mb-2'>
											<RBForm.Control
												type='email'
												value={email}
												onChange={(e) => {
													const newEmails = [...values.emails];
													newEmails[idx] = e.target.value;
													setFieldValue('emails', newEmails);
												}}
											/>
											<div
												onClick={() => {
													setFieldValue(
														'emails',
														values.emails.filter((_, i) => i !== idx),
													);
												}}>
												<img src={CloseSquare} width={30} height={30} />
											</div>
										</div>
									))}
								</Col>
							</Row>
							{/* Téléphones */}
							<Row className='mb-3'>
								<Col md={12}>
									<div className='d-flex justify-content-between align-items-center mb-2'>
										<RBForm.Label className='mb-0'>Téléphones</RBForm.Label>
										<div
											onClick={() =>
												setFieldValue('telephones', [
													...values.telephones,
													'',
												])
											}
											style={{ cursor: 'pointer' }}>
											<img
												src={AddIcon}
												width={30}
												height={30}
												alt='Ajouter Tél'
											/>
										</div>
									</div>
									{values.telephones?.map((tel, idx) => (
										<div key={idx} className='d-flex mb-2'>
											<RBForm.Control
												type='text'
												value={tel}
												onChange={(e) => {
													const newTels = [...values.telephones];
													newTels[idx] = e.target.value;
													setFieldValue('telephones', newTels);
												}}
											/>
											<div
												onClick={() => {
													setFieldValue(
														'telephones',
														values.telephones.filter(
															(_, i) => i !== idx,
														),
													);
												}}>
												<img src={CloseSquare} width={30} height={30} />
											</div>
										</div>
									))}

									<ErrorMessage
										name='telephones'
										component='div'
										className='text-danger'
									/>
								</Col>
							</Row>

							{/* Emails */}

							{/* Adresse de livraison */}
							<Row className='mb-3'>
								<Col md={12}>
									<div className='d-flex justify-content-between align-items-center mb-2'>
										<RBForm.Label className='mb-0'>
											Adresses de livraison
										</RBForm.Label>
										<div
											onClick={() =>
												setFieldValue('shippingAddresses', [
													...values.shippingAddresses,
													{
														formatted: '',
														street: '',
														city: '',
														zip: '',
													},
												])
											}
											style={{ cursor: 'pointer' }}>
											<img
												src={AddIcon}
												width={30}
												height={30}
												alt='Ajouter adresse'
											/>
										</div>
									</div>
									{values.shippingAddresses?.map((addr, idx) => (
										<div key={idx} className='mb-2 border p-2 rounded'>
											{/* <RBForm.Control
												type='text'
												placeholder='Adresse formatée'
												value={addr.formatted || ''}
												onChange={(e) => {
													const newAddrs = [...values.shippingAddresses];
													newAddrs[idx].formatted = e.target.value;
													setFieldValue('shippingAddresses', newAddrs);
												}}
												className='mb-1'
											/> */}
											<RBForm.Control
												type='text'
												placeholder='Rue'
												value={addr.street || ''}
												onChange={(e) => {
													const newAddrs = [...values.shippingAddresses];
													newAddrs[idx].street = e.target.value;
													setFieldValue('shippingAddresses', newAddrs);
												}}
												className='mb-1'
											/>
											<RBForm.Control
												type='text'
												placeholder='Ville'
												value={addr.city || ''}
												onChange={(e) => {
													const newAddrs = [...values.shippingAddresses];
													newAddrs[idx].city = e.target.value;
													setFieldValue('shippingAddresses', newAddrs);
												}}
												className='mb-1'
											/>
											<RBForm.Control
												type='text'
												placeholder='Code postal'
												value={addr.zip || ''}
												onChange={(e) => {
													const newAddrs = [...values.shippingAddresses];
													newAddrs[idx].zip = e.target.value;
													setFieldValue('shippingAddresses', newAddrs);
												}}
											/>
											<Button
												variant='danger'
												size='sm'
												onClick={() =>
													setFieldValue(
														'shippingAddresses',
														values.shippingAddresses.filter(
															(_, i) => i !== idx,
														),
													)
												}
												className='mt-1 w-100'>
												Supprimer l'adresse
											</Button>
										</div>
									))}
								</Col>
							</Row>

							{/* Autres champs */}
							<Row className='mb-3'>
								<Col md={6}>
									<RBForm.Group>
										<RBForm.Label>Interphone</RBForm.Label>
										<RBForm.Control
											type='text'
											name='interphone'
											value={values.interphone}
											onChange={handleChange}
										/>
									</RBForm.Group>
								</Col>
								<Col md={6}>
									<RBForm.Group>
										<RBForm.Label>Numéro appartement</RBForm.Label>
										<RBForm.Control
											type='text'
											name='numeroAppartement'
											value={values.numeroAppartement}
											onChange={handleChange}
										/>
									</RBForm.Group>
								</Col>
								<Col md={6}>
									<RBForm.Group>
										<RBForm.Label>Numéro propriétaire</RBForm.Label>
										<RBForm.Control
											type='text'
											name='numeroProprietaire'
											value={values.numeroProprietaire}
											onChange={handleChange}
										/>
										<ErrorMessage
											name='numeroProprietaire'
											component='div'
											className='text-danger'
										/>
									</RBForm.Group>
								</Col>
								<Col md={6}>
									<RBForm.Group>
										<RBForm.Label>Code d'accès</RBForm.Label>
										<RBForm.Control
											type='text'
											name='codeAcces'
											value={values.codeAcces}
											onChange={handleChange}
										/>
									</RBForm.Group>
								</Col>
							</Row>

							<div className='d-flex justify-content-end mt-3'>
								<Button
									type='submit'
									variant='primary'
									disabled={isSubmitting}
									className='me-2'>
									{isSubmitting && (
										<Spinner
											as='span'
											animation='border'
											size='sm'
											role='status'
											aria-hidden='true'
											className='me-2'
										/>
									)}
									Enregistrer
								</Button>

								<Button
									variant='outline-secondary'
									onClick={() => setToUpdate(false)}>
									Annuler
								</Button>
							</div>
						</Form>
					)}
				</Formik>
			) : (
				<div className='mx-3 mt-3'>
					<ClientDetailsWeb title='Nom complet' value={clientData?.nom} />

					<ClientDetailsWeb title='Occupation' value={clientData?.typeOccupation} />

					<ClientDetailsWeb
						title='Téléphones'
						value={(clientData?.telephones || []).join(', ')}
						onClick={() => handlePhoneWeb(clientData?.telephones || [])}
						icon={<SvgMobile size={24} color={COLORS.PRIMARY} />}
					/>

					<ClientDetailsWeb
						title='Adresses de livraison'
						value={(clientData?.shippingAddresses || [])
							.map((addr) => `${addr.street}, ${addr.city} ${addr.zip}`)
							.join(' • ')}
						icon={<SvgMap size={24} color={COLORS.PRIMARY} />}
						onClick={() => {
							if (clientData?.shippingAddresses[0]?.map_url) {
								window.open(addr.map_url, '_blank');
							}
						}}
					/>
					<ClientDetailsWeb
						title='Emails'
						value={(clientData?.emails || []).join(', ')}
					/>
					<ClientDetailsWeb title='Interphone' value={clientData?.interphone} />

					<ClientDetailsWeb
						title='Numéro appartement'
						value={clientData?.numeroAppartement}
					/>

					<ClientDetailsWeb
						title='Numéro propriétaire'
						value={clientData?.numeroProprietaire}
					/>

					<ClientDetailsWeb title="Code d'accès" value={clientData?.codeAcces} />
				</div>
			)}
		</div>
	);
}
export default ClientTabMobile;
const ClientDetailsWeb = ({ title, value, icon, onClick }) => {
	if (!value) return null;

	return (
		<div className='mb-5'>
			<div className='d-flex align-items-center justify-content-between'>
				<div className='d-flex flex-column'>
					<div className='mb-2' style={{ color: COLORS.GRAY }}>
						{title}
					</div>
					<div className='fw-semibold'>{value}</div>
				</div>
				{icon && (
					<div
						className='rounded-circle text-center align-content-center'
						style={{
							width: '40px',
							height: '40px',
							backgroundColor: COLORS.BG_L75_PRIMARY,
						}}
						onClick={onClick}>
						{icon}
					</div>
				)}
			</div>

			<div className='border-bottom mt-2' />
		</div>
	);
};
